import { formatDist } from './markerIcons';

// Leaflet marker-popup DOM builders shared by home (`pages/home/main.js`) and
// the pinmonkey dungeon lobby (`pages/games/pinmonkey/lobby.js`) — both
// hand-built near-identical popups via document.createElement/cssText before
// this extraction. Each builder returns the popup's root element, ready to
// pass to `.bindPopup(wrap, ...)`.

// `dungeon`: API dungeon row ({ name, slug, lat, lng, radius, distance, is_open }).
// `nav`: { navigate, isLoggedInRef, activeCharacterRef, onNeedCharacter(dungeon) }.
export const buildDungeonPopup = (colors, dungeon, nav) => {
  const inRange =
    dungeon.radius == null ||
    (dungeon.distance != null && dungeon.distance <= dungeon.radius);
  const isOpen = dungeon.is_open !== false; // null/undefined = no schedule = always open
  const canEnter = inRange && isOpen;
  const dist = dungeon.distance != null ? formatDist(dungeon.distance) : null;

  const cText = colors('text');
  const cMuted = colors('foreground', 0.45);
  const cDanger = colors('danger', 0.5);
  const cBg = colors('background');
  const cChrome = colors('chrome');
  const cHL = colors('link');
  const cDim = colors('foreground', 0.15);
  const cDimText = colors('foreground', 0.3);

  const wrap = document.createElement('div');
  wrap.style.cssText = `font-family:'Silkscreen',monospace;color:${cText};padding:0.75rem;min-width:150px`;

  const nameEl = document.createElement('div');
  nameEl.style.cssText = 'font-size:0.95rem;margin-bottom:0.2rem';
  nameEl.textContent = dungeon.name;
  wrap.appendChild(nameEl);

  if (!isOpen) {
    const closedEl = document.createElement('div');
    closedEl.style.cssText = `font-size:0.72rem;color:${cDanger};margin-bottom:0.4rem`;
    closedEl.textContent = 'Closed';
    wrap.appendChild(closedEl);
  }

  if (dist) {
    const distEl = document.createElement('div');
    distEl.style.cssText = `font-size:0.72rem;color:${
      inRange ? cMuted : cDanger
    };margin-bottom:0.6rem`;
    distEl.textContent = inRange ? dist : `${dist} — out of range`;
    wrap.appendChild(distEl);
  }

  const btn = document.createElement('button');
  btn.textContent = 'ENTER';
  if (canEnter) {
    btn.style.cssText = `background:${cChrome};border:1px solid ${cHL};color:${cText};font-family:'Silkscreen',monospace;font-size:0.8rem;padding:0.3rem 0;cursor:pointer;width:100%;letter-spacing:0.1em`;
    btn.onmouseenter = () => {
      btn.style.borderColor = cHL;
      btn.style.background = cBg;
    };
    btn.onmouseleave = () => {
      btn.style.borderColor = cHL;
      btn.style.background = cChrome;
    };
    btn.onclick = () => {
      if (!nav.isLoggedInRef.current) {
        nav.navigate('/login');
      } else if (nav.activeCharacterRef.current) {
        nav.navigate(`/games/dungeon/${dungeon.slug}`);
      } else {
        nav.onNeedCharacter(dungeon);
      }
    };
  } else {
    btn.style.cssText = `background:${cBg};border:1px solid ${cDim};color:${cDimText};font-family:'Silkscreen',monospace;font-size:0.8rem;padding:0.3rem 0;cursor:not-allowed;width:100%;letter-spacing:0.1em`;
    btn.disabled = true;
  }
  wrap.appendChild(btn);

  return wrap;
};

// `globalDungeon`: API dungeon row for the wandering/global dungeon marker.
// `nav`: same shape as `buildDungeonPopup`'s.
export const buildGlobalDungeonPopup = (colors, globalDungeon, nav) => {
  const cText = colors('text');
  const cMuted = colors('foreground', 0.45);
  const cBg = colors('background');
  const cChrome = colors('chrome');
  const cHL = colors('link');

  const wrap = document.createElement('div');
  wrap.style.cssText = `font-family:'Silkscreen',monospace;color:${cText};padding:0.75rem;min-width:150px`;

  const nameEl = document.createElement('div');
  nameEl.style.cssText = 'font-size:0.95rem;margin-bottom:0.2rem';
  nameEl.textContent = globalDungeon.name;
  wrap.appendChild(nameEl);

  const tagEl = document.createElement('div');
  tagEl.style.cssText = `font-size:0.72rem;color:${cMuted};margin-bottom:0.6rem`;
  tagEl.textContent = 'wandering dungeon';
  wrap.appendChild(tagEl);

  const btn = document.createElement('button');
  btn.textContent = 'ENTER';
  btn.style.cssText = `background:${cChrome};border:1px solid ${cHL};color:${cText};font-family:'Silkscreen',monospace;font-size:0.8rem;padding:0.3rem 0;cursor:pointer;width:100%;letter-spacing:0.1em`;
  btn.onmouseenter = () => {
    btn.style.background = cBg;
  };
  btn.onmouseleave = () => {
    btn.style.background = cChrome;
  };
  btn.onclick = () => {
    if (!nav.isLoggedInRef.current) {
      nav.navigate('/login');
    } else if (nav.activeCharacterRef.current) {
      nav.navigate(`/games/dungeon/${globalDungeon.slug}`);
    } else {
      nav.onNeedCharacter(globalDungeon);
    }
  };
  wrap.appendChild(btn);

  return wrap;
};

// `address`: API address row ({ Location: { name, slug }, name, street_address }).
// `opts`: { navigate, showClaim, onClaim }. `showClaim`/`onClaim` are optional —
// only the home-page popup shows a "CLAIM SPACE" button for logged-out users.
export const buildAddressPopup = (colors, address, opts) => {
  const cText = colors('text');
  const cMuted = colors('foreground', 0.45);
  const cBg = colors('background');
  const cChrome = colors('chrome');
  const cHL = colors('link');

  const wrap = document.createElement('div');
  wrap.style.cssText = `font-family:'Silkscreen',monospace;color:${cText};padding:0.75rem;min-width:140px`;

  const nameEl = document.createElement('div');
  nameEl.style.cssText =
    'font-size:0.9rem;margin-bottom:0.15rem;text-transform:capitalize';
  nameEl.textContent = address.Location?.name ?? address.name;
  wrap.appendChild(nameEl);

  if (address.street_address) {
    const addrEl = document.createElement('div');
    addrEl.style.cssText = `font-size:0.7rem;color:${cMuted};margin-bottom:0.5rem`;
    addrEl.textContent = address.street_address;
    wrap.appendChild(addrEl);
  }

  const btn = document.createElement('button');
  btn.textContent = 'VIEW PLACE';
  btn.style.cssText = `background:${cChrome};border:1px solid ${cHL};color:${cText};font-family:'Silkscreen',monospace;font-size:0.75rem;padding:0.25rem 0;cursor:pointer;width:100%;letter-spacing:0.1em`;
  btn.onmouseenter = () => {
    btn.style.background = cBg;
  };
  btn.onmouseleave = () => {
    btn.style.background = cChrome;
  };
  btn.onclick = () => opts.navigate(`/places/${address.Location?.slug}`);
  wrap.appendChild(btn);

  if (opts.showClaim) {
    const claimBtn = document.createElement('button');
    claimBtn.textContent = 'CLAIM SPACE';
    claimBtn.style.cssText = `background:${cChrome};border:1px solid ${cHL};color:${cText};font-family:'Silkscreen',monospace;font-size:0.75rem;padding:0.25rem 0;cursor:pointer;width:100%;letter-spacing:0.1em;margin-top:0.4rem`;
    claimBtn.onmouseenter = () => {
      claimBtn.style.background = cBg;
    };
    claimBtn.onmouseleave = () => {
      claimBtn.style.background = cChrome;
    };
    claimBtn.onclick = () => opts.onClaim(address);
    wrap.appendChild(claimBtn);
  }

  return wrap;
};
